import * as Sentry from '@sentry/react';
import { logger } from 'utils/logger';

export type TelemetryLevel = 'info' | 'warn' | 'error';

export interface TelemetryEvent {
  event: string;
  message: string;
  level: TelemetryLevel;
  channel: string;
  attributes?: Record<string, unknown>;
  visibility?: 'fleet' | 'internal';
  cap?: number;
}

const DEFAULT_CAP = 50;

// Per-page, never per-instance: a reconnect loop is exactly what a cap protects against.
let seqByEvent: Record<string, number> = {};

// Sentry Logs serializes null to the STRING "null", which mixes types on the fields you
// cohort by. An absent key queries cleanly.
export const logsAttributes = <T extends object>(payload: T): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== null && value !== undefined)
  );

export const emitTelemetryEvent = ({
  event,
  message,
  level,
  channel,
  attributes = {},
  visibility = 'fleet',
  cap = DEFAULT_CAP,
}: TelemetryEvent): void => {
  const seq = (seqByEvent[event] ?? 0) + 1;
  if (seq > cap) {
    return;
  }
  seqByEvent[event] = seq;

  const record = { ...attributes, seq, release: process.env.REACT_APP_RELEASE_VERSION };

  logger
    .remote({ system: true, capture: channel, nonProd: visibility === 'internal' })
    [level](message, record);

  // Safe no-op when Sentry.init never ran (sentryEnabled=false rooms).
  Sentry.logger[level](message, logsAttributes({ ...record, event }));
};

export const resetTelemetryCaps = (): void => {
  seqByEvent = {};
};
